import React, { useState, useEffect } from "react";
import { DataTable, Filter, PageWrapper } from "../../components";
import { Dropdown } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { DriverType, OrderStatus, Pages } from "../../shared/enums";
import { useTranslation } from "react-i18next";
import * as XLSX from 'xlsx';
import dayjs from "dayjs";
import { getOrderList } from "../../api/order";
import { DATETIME_FORMAT } from "../../shared/constants";
import { formatFilterToQuery } from "../../shared/utils";

const Order = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [filter, setFilter] = useState({});
  const [pagination, setPagination] = useState({ page: 1, pageSize: 25 });
  const [totalPages, setTotalPage] = useState(0);
  const [data, setData] = useState([]);

  const formatTips = (tips, currency) =>
    tips == null || tips === 0 ? "-" : `${currency || ""} ${Number(tips).toFixed(2)}`.trim();

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to page 1 when filter changes
  };

  const columns = [
    {
      key: "createdAt",
      label: t("CREATED_AT"),
      render: (createdAt, data) => dayjs(createdAt).format(DATETIME_FORMAT),
    },
    { key: "driverType", label: t("DRIVER_TYPE") },
    { key: "currency", label: t("CURRENCY") },
    {
      key: "total",
      label: t("TOTAL"),
      render: (amount, data) => (+(data.amount || 0) + +(data.sstAmount || 0)).toFixed(2),
    },
    {
      key: "sstAmount",
      label: t("SST"),
      render: (sstAmount) => Number(sstAmount || 0).toFixed(2),
    },
    {
      key: "companyCommissionPercentage",
      label: t("CHARGES_PERCENTAGE"),
      render: (companyCommissionPercentage, data) =>
        companyCommissionPercentage
          ? `${companyCommissionPercentage}%`
          : data.companyCommission && data.amount
            ? `${((data.companyCommission / data.amount) * 100).toFixed(2)}%`
            : "-",
    },
    {
      key: "companyCommission",
      label: t("CHARGES_AMOUNT"),
      render: (companyCommission) => Number(companyCommission || 0).toFixed(2),
    },
    { key: "status", label: t("STATUS") },
    { key: "userFullName", label: t("USER_FULL_NAME") },
    {
      key: "userPhoneNumber",
      label: t("USER_PHONE_NUMBER"),
      render: (phoneNumber, data) =>
        data.userCountryCode + data.userPhoneNumber || "-",
    },
    { key: "driverFullName", label: t("DRIVER_FULL_NAME"), render: (fullName, data) => data.driverFullName || "-" },
    {
      key: "driverPhoneNumber",
      label: t("DRIVER_PHONE_NUMBER"),
      render: (phoneNumber, data) =>
        data.driverCountryCode + data.driverPhoneNumber || "-",
    },
    {
      key: "rating",
      label: t("RATING"),
      render: (rating) => rating ?? "-",
    },
    {
      key: "comment",
      label: t("COMMENT"),
      render: (comment) => comment || "-",
    },
    {
      key: "tips",
      label: t("TIPS"),
      render: (tips, data) => formatTips(tips, data.currency),
    },
    { key: "refNo", label: t("REFERENCE_NO") },
    {
      key: "action", label: t("ACTION"), render: (v, data) => <Dropdown>
        <Dropdown.Toggle variant="primary" size="sm" className='py-0'></Dropdown.Toggle>
        <Dropdown.Menu>
          <Dropdown.Item onClick={() => navigate(Pages.OrderDetail.replace(":id", data.id))}>{t("DETAIL")}</Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    }
  ];

  const filters = [
    { type: "input", label: t("SEARCH"), name: "search" },
    { type: "datetime", label: t("FORM_DATE"), name: "fromDate" },
    { type: "datetime", label: t("TO_DATE"), name: "toDate" },
    { type: "select", label: t("STATUS"), name: "status", options: [{ label: "All", value: "" }, ...Object.entries(OrderStatus).map(([key, value]) => ({ label: t(key), value: value }))] },
    { type: "select", label: t("DRIVER_TYPE"), name: "driverType", options: [{ label: "All", value: "" }, ...Object.entries(DriverType).map(([key, value]) => ({ label: t(key), value: value }))] }
  ];

  useEffect(() => {
    getData(pagination, filter);
  }, [pagination, filter]);

  const getData = async (pagination, filter) => {
    const response = await getOrderList({
      skip: (pagination?.page - 1) * pagination?.pageSize,
      limit: pagination?.pageSize,
      ...formatFilterToQuery(filter, [{ keys: ["fromDate", "toDate"], formatter: (value) => dayjs(value).format() }]),
    });
    setData(response.items);
    setTotalPage(Math.ceil(response?.pagination?.total / pagination?.pageSize));
  };

  const getExportData = () => data.map(item => ({
    [t("CREATED_AT")]: dayjs(item.createdAt).format(DATETIME_FORMAT),
    [t("DRIVER_TYPE")]: item.driverType,
    [t("CURRENCY")]: item.currency,
    [t("AMOUNT")]: ((item.amount || 0)).toFixed(2),
    [t("SST")]: ((item.sstAmount || 0)).toFixed(2),
    [t("CHARGES_PERCENTAGE")]: item.companyCommissionPercentage
      ? `${item.companyCommissionPercentage}%`
      : item.companyCommission && item.amount
        ? `${((item.companyCommission / item.amount) * 100).toFixed(2)}%`
        : "-",
    [t("CHARGES_AMOUNT")]: ((item.companyCommission || 0)).toFixed(2),
    [t("TOTAL")]: ((item.amount || 0) + (item.sstAmount || 0)).toFixed(2),
    [t("STATUS")]: item.status,
    [t("USER_FULL_NAME")]: item.userFullName,
    [t("USER_PHONE_NUMBER")]: item.userCountryCode + item.userPhoneNumber || "-",
    [t("DRIVER_FULL_NAME")]: item.driverFullName || "-",
    [t("DRIVER_PHONE_NUMBER")]: item.driverCountryCode + item.driverPhoneNumber || "-",
    [t("RATING")]: item.rating ?? "-",
    [t("COMMENT")]: item.comment || "-",
    [t("TIPS")]: formatTips(item.tips, item.currency),
    [t("REFERENCE_NO")]: item.refNo,
  }));

  const exportToCsv = () => {
    const worksheet = XLSX.utils.json_to_sheet(getExportData());
    const csv = XLSX.utils.sheet_to_csv(worksheet);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `orders_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  return (
    <PageWrapper headerProps={{ title: "ORDER" }}>
      <Filter
        filters={filters}
        onChangeFilter={handleFilterChange}
        onExport={exportToCsv}
        exportDisabled={data.length === 0}
      />
      <DataTable
        columns={columns}
        data={data}
        pagination={{ ...pagination, totalPages, onChange: setPagination }}
      />
    </PageWrapper>
  );
};

export default Order;
